import styled from "styled-components";
import NotificationItem from "features/notifications/NotificationItem";
import VirtualList from "react-tiny-virtual-list";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Empty from "common/Empty/Empty";
import Text from "common/Typography/Text";
import { useMemo, useState } from "react";
import useNotificationsList from "@shared/features/notifications/useNotificationsList";
import { NotificationScreens } from "@shared/types";
import useNotificationsBanners from "@shared/features/notifications/useNotificationsBanners";
import { sharedLib } from "@shared/services/shared.service";

const TOP_BOTTOM_PADDING = 16;
const DEFAULT_ITEM_HEIGHT = 80;
const LIST_HEIGHT = 400;

interface NotificationsListProps {
  closeModal: () => void;
}

const NotificationsList = ({ closeModal }: NotificationsListProps) => {
  const { data: notifications, isFetching } =
    sharedLib.sharedApi.notifications.endpoints.getNotifications.useQueryState(
      undefined
    );
  const [itemsHeights, setItemsHeights] = useState<{ [key: number]: number }>(
    {}
  );

  const setItemHeight = (index: number, height: number) => {
    setItemsHeights((prevItemsHeights) => {
      return { ...prevItemsHeights, [index]: height };
    });
  };

  const getItemHeight = (index: number) => {
    const itemContentHeight = itemsHeights[index];
    return itemContentHeight
      ? itemContentHeight + TOP_BOTTOM_PADDING * 2
      : DEFAULT_ITEM_HEIGHT;
  };

  const { sortedNotificationsArray, numNotifications } =
    useNotificationsList(notifications);
  const tradesBanners = useNotificationsBanners(sortedNotificationsArray);

  const firstUpgradeNotificationIndex = useMemo(
    () =>
      sortedNotificationsArray?.findIndex((notifications) => {
        return notifications[0].screen === NotificationScreens.UPGRADE_PACKAGE;
      }),
    [sortedNotificationsArray]
  );

  return (
    <Wrapper>
      <LoadingSpinnerOverlay
        isLoading={isFetching}
        spinnerMarginTop={50}
        hideContentWhileLoading={true}
      >
        {notifications &&
          (numNotifications === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ marginTop: 30 }}
              description={<Text type={"secondary"}>No notifications</Text>}
            />
          ) : (
            <VirtualList
              renderItem={({ index, style }) => {
                const notification = sortedNotificationsArray?.[index] || [];
                return (
                  <NotificationItem
                    style={style}
                    key={index}
                    notification={notification}
                    setItemHeight={(height: number) =>
                      setItemHeight(index, height)
                    }
                    showUpgrade={index === firstUpgradeNotificationIndex}
                    closeModal={closeModal}
                    bannerImageLink={
                      tradesBanners?.[notification?.[0].component_id]
                    }
                  />
                );
              }}
              width="100%"
              height={LIST_HEIGHT}
              itemCount={numNotifications || 0}
              itemSize={getItemHeight}
            />
          ))}
      </LoadingSpinnerOverlay>
    </Wrapper>
  );
};

export default NotificationsList;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: ${LIST_HEIGHT}px;
  overflow: auto;
  position: relative;
`;
